// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import { Spinner } from '../../../common/component/Spinner'
import { IContractDto, IContractNestedDto } from '../Contract.model'
import { useContractGetByIdMutation } from '../Contract.api'
import { IconLabelContent } from '../../../common/component/IconLabelContent'
import { selectContract } from '../Contract.slice'
import { useAppSelector } from '../../store/Store'

interface Props {
  contract: IContractNestedDto
}

export const ContractEmbedered: FC<Props> = ({contract}: Props) => {
  const intl = useIntl()
  const storeData = useAppSelector(selectContract)
  const didFetchData = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IContractDto>()
  const [getById] = useContractGetByIdMutation()

  useEffect(() => {
    const fetchData = async (id) => {
      try {
        if (!didFetchData.current) {
          const response: any = await getById({id})
          if (!response || response.error) {
            setError(intl.formatMessage({id: 'ERROR.MESSAGE.NOT_AVAILABLE'}))
          } else {
            setData(response.data)
          }
        }
      } catch (e: any) {
        setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsLoading(false)
      }
    }
    if (storeData && storeData.id === contract.id) {
      setData(storeData)
      setIsLoading(false)
    } else {
      fetchData(contract.id)
    }
  }, [getById, contract, intl, storeData])

  const buildDescriptionLine = (data: IContractDto) => {
    let description = ``
    if (data.client?.displayName) {
      description += description ? ` | ${data.client?.displayName}` : `${data.client?.displayName}`
    }
    if (data.serviceType) {
      description += description ? ` | ${data.serviceType}` : `${data.serviceType}`
    }
    if (data.serviceFrequency) {
      description += description ? ` | ${data.serviceFrequency}` : `${data.serviceFrequency}`
    }
    if (data.serviceLocation) {
      description += description ? ` | ${data.serviceLocation}` : `${data.serviceLocation}`
    }

    return description
  }

  const builStatusLine = (data: IContractDto) => {
    if (data.status && data.startDate && data.endDate) {
      return `${intl.formatMessage({id: `UTIL.STATUS.${data.status}`})} | ${
        new Date(data.startDate).toISOString().split('T')[0]
      } - ${new Date(data.endDate).toISOString().split('T')[0]}`
    } else if (data.status) {
      return `${intl.formatMessage({id: `UTIL.STATUS.${data.status}`})}`
    } else {
      return '<SIN DATOS>'
    }
  }

  return (
    <div className='row mb-5'>
      <>
        <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
          <span className='text-gray-700'>{intl.formatMessage({id: 'UTIL.CONTRACT'})}</span>
        </label>

        <div className='col-xxl-9'>
          {!data && isLoading ? (
            <Spinner />
          ) : data ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7 cursor-pointer'>
                      <Link className='' to={`/contracts/${data.id}`} target='_blank'>
                        <span className='fw-bolder mb-2'>{`${data.documentType}`}</span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='text-primary mb-2'>{data.sequence}</span>
                      </Link>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>{buildDescriptionLine(data)}</span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start align-items-center'>
                      <IconLabelContent
                        title={builStatusLine(data)}
                        active={data.status === 'ACTIVE'}
                        icon={`${
                          data.status === 'ACTIVE' ? 'bi bi-calendar2-check' : 'bi bi-calendar2-x'
                        }`}
                        inactiveColor='text-danger'
                      />

                      <IconLabelContent
                        title={
                          data.totalAttachment > 0
                            ? intl
                                .formatMessage({
                                  id:
                                    data.totalAttachment === 1
                                      ? 'TITLE.ATTACHMENT.TOTAL_SINGLE'
                                      : 'TITLE.ATTACHMENT.TOTAL_MULTIPLE',
                                })
                                .replace(/%s/g, `${data.totalAttachment}`)
                            : intl.formatMessage({id: 'TITLE.ATTACHMENT.ZERO'})
                        }
                        active={data.totalAttachment > 0}
                        labelValue={`${data.totalAttachment || 0}`}
                        icon='bi bi-paperclip'
                      />

                      <IconLabelContent
                        title={`${intl
                          .formatMessage({id: 'TITLE.ASSET.TOTAL'})
                          .replace(/%s/g, `${data.totalAsset > 0 ? data.totalAsset : 0}`)}`}
                        labelValue={`${data.totalAsset || 0}`}
                        active={data.totalAsset > 0}
                        icon='bi bi-cpu'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7'>
                      <span className='fw-bolder mb-2'>{`${
                        contract.documentType || intl.formatMessage({id: 'UTIL.CONTRACT'})
                      }`}</span>
                      <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                      <span className='text-primary mb-2'>{contract.sequence}</span>
                    </div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
      {/* <CustomInputRenderer
        label={intl.formatMessage({id: 'UTIL.CONTRACT'})}
        object={
          !data && isLoading ? (
            <Spinner />
          ) : data ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='lh-2 fs-7 cursor-pointer'>
                      <Link className='' to={`/contracts/${data.id}`} target='_blank'>
                        <span className='fw-bolder mb-2'>{`${data.documentType}`}</span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='text-primary mb-2'>{data.sequence}</span>
                      </Link>
                    </div>

                    <div className='mt-1 lh-1'>
                      <div className='d-flex justify-content-start'>
                        <div className='d-flex align-items-center fs-8'>
                          <span className='text-gray-600'>{buildDescriptionLine(data)}</span>
                        </div>
                      </div>
                    </div>

                    <div className='mt-1 lh-1'>
                      <div className='d-flex justify-content-start align-items-center'>
                        <IconLabelContent
                          title={builStatusLine(data)}
                          active={data.status === 'ACTIVE'}
                          icon={`${
                            data.status === 'ACTIVE' ? 'bi bi-calendar2-check' : 'bi bi-calendar2-x'
                          }`}
                          inactiveColor='text-danger'
                        />

                        <IconLabelContent
                          title={
                            data.totalAttachment > 0
                              ? intl
                                  .formatMessage({
                                    id:
                                      data.totalAttachment === 1
                                        ? 'TITLE.ATTACHMENT.TOTAL_SINGLE'
                                        : 'TITLE.ATTACHMENT.TOTAL_MULTIPLE',
                                  })
                                  .replace(/%s/g, `${data.totalAttachment}`)
                              : intl.formatMessage({id: 'TITLE.ATTACHMENT.ZERO'})
                          }
                          active={data.totalAttachment > 0}
                          labelValue={`${data.totalAttachment || ''}`}
                          icon='bi bi-paperclip'
                        />

                        <IconLabelContent
                          title={`${intl
                            .formatMessage({id: 'TITLE.ASSET.TOTAL'})
                            .replace(/%s/g, `${data.totalAsset > 0 ? data.totalAsset : 0}`)}`}
                          labelValue={`${data.totalAsset || ''}`}
                          active={data.totalAsset > 0}
                          icon='bi bi-cpu'
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='lh-2 fs-7'>
                      <span className='fw-bolder mb-2'>{`${contract.documentType}`}</span>
                      <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                      <span className='text-primary mb-2'>{contract.sequence}</span>
                    </div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></CustomInputRenderer> */}
    </div>
  )
}
